import { useState } from 'react';
import type { FC, PropsWithChildren } from 'react';

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { QueryClient } from '@tanstack/react-query';
import { useIsRestoring } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { useTranslation } from 'react-i18next';
import { Loader } from '@cognite/cogs-core-v11/components/loader/loader.tsx';

import { useChecklistItemsUpsert } from '../../features/checklist/hooks/use-mutation/use-checklist-items-upsert-mutation';
import { useChecklistUpsertMutation } from '../../features/checklist/hooks/use-mutation/use-checklist-upsert-mutation';
import { useMeasurementNumericReadingUpsert } from '../../features/measurements/hooks/use-mutation/use-measurement-numeric-reading-upsert';
import { useMeasurementsUpsert } from '../../features/measurements/hooks/use-mutation/use-measurement-upsert-mutation';
import { MutationKeys } from '../../utils/queryKeys';

import * as S from './elements';
import { REACT_QUERY_PERSISTER_KEY } from './utils';

type Props = {
  client: QueryClient;
};

export const PersistQueryFunctionsProvider: FC<PropsWithChildren<Props>> = ({
  client,
  children,
}) => {
  const { t } = useTranslation('infobar');
  const [isDataRestored, setIsDataRestored] = useState(false);

  const persister = createAsyncStoragePersister({
    key: REACT_QUERY_PERSISTER_KEY,
    storage: window.localStorage,
  });
  const isRestoringData = useIsRestoring();

  // list all mutate functions that you want to persist in local storage below
  const { mutateAsync: upsertChecklistItemsFn } = useChecklistItemsUpsert();
  const { mutateAsync: upsertChecklistFn } = useChecklistUpsertMutation();
  const { mutateAsync: upsertMeasurementFn } = useMeasurementsUpsert();
  const { mutateAsync: upsertReadingFn } = useMeasurementNumericReadingUpsert();
  client.setMutationDefaults([MutationKeys.USE_OPERATIONS_UPSERT], {
    mutationFn: (variables: Parameters<typeof upsertChecklistItemsFn>[0]) =>
      upsertChecklistItemsFn(variables),
  });
  client.setMutationDefaults([MutationKeys.UPSERT_CHECKLIST], {
    mutationFn: (variables: Parameters<typeof upsertChecklistFn>[0]) =>
      upsertChecklistFn(variables),
  });
  client.setMutationDefaults([MutationKeys.USE_MEASURMENT_UPSERT], {
    mutationFn: (variables: Parameters<typeof upsertMeasurementFn>[0]) =>
      upsertMeasurementFn(variables),
  });
  client.setMutationDefaults(
    [MutationKeys.USE_MEASURMENT_NUMERIC_READING_UPSERT],
    {
      mutationFn: (variables: Parameters<typeof upsertReadingFn>[0]) =>
        upsertReadingFn(variables),
    }
  );

  return (
    <PersistQueryClientProvider
      client={client}
      persistOptions={{
        persister,
        dehydrateOptions: {
          shouldDehydrateQuery: () => false,
          shouldDehydrateMutation: (mutation) => mutation.state.isPaused,
        },
      }}
      onSuccess={() =>
        client.resumePausedMutations().then(() => {
          setIsDataRestored(true);
        })
      }
    >
      {!isDataRestored || isRestoringData ? (
        <Loader
          infoText={
            <S.MessageContentWrapper>
              {t(
                'NETWORK_STATUS_SYNCING_OFFLINE_CHANGES',
                'Keep InField open while we save the changes you made when you were offline'
              )}
            </S.MessageContentWrapper>
          }
        />
      ) : (
        children
      )}
    </PersistQueryClientProvider>
  );
};
